import React, { useState, useEffect, useRef, useCallback } from 'react';
import { aiApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Robot, X, PaperPlaneTilt, Trash, CircleNotch, Sparkle
} from '@phosphor-icons/react';

export default function AIChatSidebar({ aiEnabled }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = `chat-${user?.email || 'default'}`;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Load history when opened
  useEffect(() => {
    if (open && messages.length === 0) {
      setLoadingHistory(true);
      aiApi.getChatHistory(sessionId).then(res => {
        setMessages((res.data || []).map(m => ({
          role: m.role, content: m.content, id: m.id,
        })));
      }).catch(() => {}).finally(() => setLoadingHistory(false));
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await aiApi.chatStream({ message: userMsg, session_id: sessionId });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let aiResponse = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'delta') {
                aiResponse += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: aiResponse };
                  return updated;
                });
              } else if (data.type === 'error') {
                aiResponse = `Error: ${data.content}`;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: aiResponse };
                  return updated;
                });
              }
            } catch {}
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const handleClear = async () => {
    await aiApi.clearChatHistory(sessionId);
    setMessages([]);
  };

  if (!aiEnabled) return null;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button data-testid="ai-chat-toggle" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center group">
          <Sparkle size={26} weight="fill" className="group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {/* Sidebar Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[560px] flex flex-col rounded-2xl shadow-2xl border border-[var(--border)] overflow-hidden bg-[var(--background)]"
          data-testid="ai-chat-panel">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-violet-600 text-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Robot size={22} weight="fill" />
              <div>
                <h3 className="text-sm font-bold">AI Assistant</h3>
                <p className="text-[10px] opacity-80">Gemini 3.5 Flash</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="ai-chat-clear" onClick={handleClear}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Clear chat">
                <Trash size={16} />
              </button>
              <button data-testid="ai-chat-close" onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {loadingHistory && (
              <div className="text-center py-4">
                <CircleNotch size={20} className="animate-spin mx-auto text-[var(--text-muted)]" />
              </div>
            )}
            {messages.length === 0 && !loadingHistory && (
              <div className="text-center py-8">
                <Sparkle size={32} className="mx-auto text-blue-500/40 mb-2" />
                <p className="text-sm text-[var(--text-secondary)]">Ask me anything about your project</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">Planning, scheduling, construction methods...</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--border)] rounded-bl-sm'
                }`}>
                  {m.content}
                  {m.role === 'assistant' && m.content === '' && loading && (
                    <CircleNotch size={16} className="animate-spin text-blue-500" />
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-[var(--border)] flex-shrink-0">
            <div className="flex items-center gap-2">
              <input ref={inputRef} data-testid="ai-chat-input"
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask about your project..."
                className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                disabled={loading} />
              <button data-testid="ai-chat-send" onClick={handleSend} disabled={!input.trim() || loading}
                className="p-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
